import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Box, Divider, MenuItem, TextField } from '@mui/material';
import { useToast } from 'use-toast-mui';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import * as XLSX from 'xlsx';
import { pageSize } from '../../../constants/constants';
import { FormInputDate } from '../../../form-components/FormInputDate';
import AnimationPage from '../../../pages/AnimationPage';
import FilterButtons from '../../FilterButtons';
import { FormInputDropdown } from '../../form/FormInputDropdown';
import Loader from '../../loader';
import TemporaryUserListTable from './TemporaryUserListTable';
import SmsModal from '../../member/modal/sms/SmsModal';
import { SMSButton } from '../../member/memberList/MemberListStyled';
import SmsIcon from '../../../_icons/SmsIcon';
import LightVersionOverlay, { useTransientLightVersionOverlay } from '../../common/LightVersionOverlay';
import { getTemporaryUsersList, getTemporaryUserSummary } from '../../../api/temporaryUser';
import useConsultOptions from '../../../hooks/useConsultOptions';
import { formatVisitPath } from '../../../constants/visitPath';
import { getConsultMethodLabel } from '../../../constants/consultation';
import { errorMessages } from '../../../utils/methods';
import {
  FilterGrid,
  FilterBlock,
  FilterBlockCompact,
  PeriodInputWrapper,
  FilterActionRow,
  TableActionRow,
  PageSizeWrapper,
  LoaderWrapper,
  SummaryCardRow,
  SummaryCard,
  SummaryCardLabel,
  SummaryCardValue,
  FilterSectionTitle,
  FilterSectionTitleText,
  TotalCountText,
  TotalCountNumber,
} from '../TemporaryUserStyled';
import {
  FilterContainer,
  FilterHeader,
  FilterTitle,
  FilterContent,
  CollapseIcon,
  SearchInputWrapper,
  SearchInput,
  ChipsContainer,
  FilterChip,
  StyledSelect,
} from '../../member/MemberFilter/MemberFilterStyled';
import CommonFilterActionButton from '../../../form-components/CommonFilterActionButton';
import CommonFilterToggle from '../../../form-components/CommonFilterToggle';
import { CommonExcelButton } from '../../../form-components/CommonExcelButton';
import CommonPagination from '../../../form-components/CommonPagination';
import CommonSearchSubmitButton from '../../../form-components/CommonSearchSubmitButton';
import CommonSelect from '../../../form-components/CommonSelect';

const GENDER_CHIPS = [
  { value: null, label: '전체' },
  { value: 'MALE', label: '남자' },
  { value: 'FEMALE', label: '여자' },
];

// 전체선택·엑셀 전체 조회 상한. 회원목록과 동일하게 맞춘다.
const FETCH_ALL_LIMIT = 10000;

const GENDER_LABELS = { MALE: '남성', FEMALE: '여성' };

const CONVERSION_RATE_LABELS = {
  VERY_HIGH: '아주높음',
  HIGH: '높음',
  MEDIUM: '중간',
  LOW: '낮음',
  VERY_LOW: '아주낮음',
};

const CONVERSION_RATE_OPTIONS = [
  { label: '아주높음', value: 'VERY_HIGH' },
  { label: '높음', value: 'HIGH' },
  { label: '중간', value: 'MEDIUM' },
  { label: '낮음', value: 'LOW' },
  { label: '아주낮음', value: 'VERY_LOW' },
];

const APP_LINKED_OPTIONS = [
  { value: 'Y', label: '연동' },
  { value: 'N', label: '미연동' },
];

const AGE_RANGE_OPTIONS = [
  { value: '', label: '연령대를 선택해 주세요.' },
  { value: 'UNDER_30', label: '30세 미만' },
  { value: 'AGE_30_40', label: '30~40세' },
  { value: 'AGE_40_50', label: '40~50세' },
  { value: 'AGE_50_60', label: '50~60세' },
  { value: 'OVER_60', label: '60세 이상' },
];

const TemporaryUserList = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const location = useLocation();
  const user = useSelector(state => state.user.user);
  const token = user?.token;
  const userBranch = user?.branch;
  const userHead = user?.headBranch;
  const isGuestUser = user?.userRole === 'ROLE_GUEST';
  const liteOverlay = useTransientLightVersionOverlay();

  const searchParams = new URLSearchParams(location.search);
  const urlSearchInput = searchParams.get('searchInput');

  const initialQuery = {
    page: 1,
    limit: 10,
    status: '',
    searchInput: urlSearchInput || '',
    registeredDateFrom: '',
    registeredDateTo: '',
    headBranchId: userHead?.headBranchId,
    branchId: userBranch?.branchId,
    genders: [],
    ageRanges: [],
    conversionRate: '',
    visitPath: '',
    consultMethod: '',
    exercisePurpose: '',
    appLinked: '',
    sortBy: 'registeredDate',
    sortDirection: 'DESC',
  };

  const [query, setQuery] = useState(() => {
    const saved = sessionStorage.getItem('temporaryUserSearchConditions');
    if (saved) {
      sessionStorage.removeItem('temporaryUserSearchConditions');
      try { return JSON.parse(saved); } catch { return initialQuery; }
    }
    return initialQuery;
  });
  // 선택된 잠재회원의 단일 소스. userId → 회원 객체.
  // 체크 여부만 들고 있으면 페이지를 넘겼을 때 그 페이지 밖의 선택을 엑셀·SMS로 넘길 수 없다.
  const [selectedMap, setSelectedMap] = useState(() => new Map());
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const selectAllInFlight = useRef(false);
  // 전량 조회 세대 번호. 응답을 기다리는 사이 검색조건이 바뀌면 결과를 버린다.
  const selectAllGeneration = useRef(0);
  const [search, setSearch] = useState(urlSearchInput || '');
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [smsModalOpen, setSmsModalOpen] = useState(false);

  const { handleSubmit, control, reset, setValue, watch } = useForm({ defaultValues: initialQuery });
  const { visitPaths, consultMethods, exercisePurposes } = useConsultOptions();

  const canFetch = !!(userHead?.headBranchId && (user.centerType !== 'BRANCH' || userBranch?.branchId));

  const { data: summary } = useQuery({
    queryKey: ['temporaryUserSummary', query.headBranchId, query.branchId],
    queryFn: () =>
      getTemporaryUserSummary(qs.stringify({ headBranchId: query.headBranchId, branchId: query.branchId })),
    enabled: !!token && canFetch,
  });

  const { data: managers, isLoading } = useQuery({
    queryKey: ['temporaryUsers', query],
    queryFn: () => getTemporaryUsersList(token, qs.stringify(query)),
    enabled: !!token && canFetch,
  });

  const managersWithCheck = managers
    ? {
        ...managers,
        content: managers.content?.map(u => ({ ...u, isChecked: selectedMap.has(u.userId) })),
      }
    : null;

  const selectedUsers = useMemo(() => [...selectedMap.values()], [selectedMap]);

  // 검색조건이 바뀌면 선택을 비운다. page만 바뀐 경우는 조건 변경이 아니다.
  // (이 화면은 필터를 건드리는 즉시 재조회하므로 query가 곧 적용된 조건이다)
  const querySignature = useMemo(() => {
    const { page, ...rest } = query;
    return JSON.stringify(rest);
  }, [query]);

  useEffect(() => {
    selectAllGeneration.current += 1;
    setSelectedMap(new Map());
  }, [querySignature]);

  // URL의 searchInput 쿼리 파라미터를 검색 input 값으로 동기화 (Header 검색 이동 대응)
  useEffect(() => {
    if (urlSearchInput) {
      setSearch(urlSearchInput);
    }
  }, [location.search, urlSearchInput]);

  // 지점 미선택 안내
  useEffect(() => {
    if (!canFetch) {
      toast.warning(t('Please select a point'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch]);

  // 본사/지점이 변경되면 query에 반영 (Header에서 드롭다운 변경 시 재조회)
  useEffect(() => {
    setQuery(prev => ({
      ...prev,
      headBranchId: userHead?.headBranchId,
      branchId: userBranch?.branchId,
    }));
  }, [userHead?.headBranchId, userBranch?.branchId]);

  const onSubmitForm = handleSubmit(values => {
    const from = new Date(values.registeredDateFrom);
    const to = new Date(values.registeredDateTo);
    const registeredDateFrom = values.registeredDateFrom ? from.toISOString().split('T')[0] : '';
    const registeredDateTo = values.registeredDateTo ? to.toISOString().split('T')[0] : '';

    setQuery(prev => ({
      ...prev,
      searchInput: search,
      registeredDateFrom,
      registeredDateTo,
      headBranchId: userHead?.headBranchId,
      branchId: userBranch?.branchId,
      page: 1,
    }));
  });

  const handleChangePage = (_, value) => {
    setQuery(prev => ({ ...prev, page: value }));
  };

  const changeLimit = event => {
    setQuery(prev => ({ ...prev, limit: event.target.value, page: 1 }));
  };

  const handleReset = () => {
    setQuery(initialQuery);
    reset(initialQuery);
    setSearch('');
  };

  const handleGenderClick = value => {
    if (value === null) {
      setQuery(prev => ({ ...prev, genders: [] }));
      return;
    }
    setQuery(prev => ({
      ...prev,
      genders: prev.genders.includes(value) ? prev.genders.filter(g => g !== value) : [...prev.genders, value],
    }));
  };

  const isGenderActive = value => {
    if (value === null) return query.genders.length === 0;
    return query.genders.includes(value);
  };

  const handleAgeRangeChange = event => {
    const value = event.target.value;
    setQuery(prev => ({
      ...prev,
      ageRanges: value === '' ? [] : [value],
    }));
  };

  const handleConversionRateChange = event => {
    setQuery(prev => ({ ...prev, conversionRate: event.target.value, page: 1 }));
  };

  const handleFilterChange = name => event => {
    setQuery(prev => ({ ...prev, [name]: event.target.value, page: 1 }));
  };

  const handleSortChange = newDirection => {
    setQuery(prev => ({ ...prev, sortDirection: newDirection, page: 1 }));
  };

  // 헤더 체크박스: 현재 검색조건의 전체 잠재회원을 담거나(체크) 선택을 모두 비운다(해제).
  const handleSelectAll = async checked => {
    if (!checked) {
      setSelectedMap(new Map());
      return;
    }
    // CommonCheckbox는 root onClick과 input onChange가 모두 걸려 클릭 1회에 핸들러가 2번 불린다.
    if (selectAllInFlight.current) return;
    selectAllInFlight.current = true;
    const generation = selectAllGeneration.current;
    setSelectAllLoading(true);
    try {
      const result = await getTemporaryUsersList(token, qs.stringify({ ...query, page: 1, limit: FETCH_ALL_LIMIT }));
      // 조회 중 검색조건이 바뀌었으면 이전 조건의 결과로 선택을 덮어쓰지 않는다.
      if (selectAllGeneration.current !== generation) return;
      setSelectedMap(new Map((result?.content || []).map(u => [u.userId, u])));
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      selectAllInFlight.current = false;
      setSelectAllLoading(false);
    }
  };

  const handleChange = e => {
    const { name, checked } = e.target;
    if (name === 'allSelect') {
      handleSelectAll(checked);
      return;
    }
    const target = managersWithCheck?.content?.find(u => u.name + u.userId === name);
    if (!target) return;
    setSelectedMap(prev => {
      const next = new Map(prev);
      if (checked) next.set(target.userId, target);
      else next.delete(target.userId);
      return next;
    });
  };

  const exportToExcel = async () => {
    // 선택이 있으면 페이지와 무관하게 선택 전원, 없으면 현재 검색조건 전체를 내려받는다.
    if (selectedMap.size > 0) {
      downloadExcel(selectedUsers);
      return;
    }
    try {
      const result = await getTemporaryUsersList(token, qs.stringify({ ...query, page: 1, limit: FETCH_ALL_LIMIT }));
      downloadExcel(result?.content || []);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const downloadExcel = rows => {
    // 목록 테이블에 보이는 항목 순서를 그대로 따른다 (+ 나이).
    const filteredData = (rows || []).map(row => ({
      지점: row.branchName || '',
      이름: row.name || '',
      성별: GENDER_LABELS[row.gender] || '',
      나이: row.age ?? '',
      생년월일: row.birthDate || '',
      전화번호: row.phoneNumber || '',
      이메일: row.email || '',
      방문경로: row.visitPath ? formatVisitPath(row.visitPath, row.visitPathEtc) : '',
      상담수단: row.consultMethod ? getConsultMethodLabel(row.consultMethod) : '',
      메모: row.memo || '',
      '전환 확률': CONVERSION_RATE_LABELS[row.conversionRate] || '',
      등록일: row.registeredDate || '',
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(filteredData);
    XLSX.utils.book_append_sheet(wb, ws, 'TemporaryUsers');
    XLSX.writeFile(wb, 'temporary_users.xlsx');
  };

  const isExcelDisabled = isLoading || selectAllLoading || !managersWithCheck?.content?.length;

  return (
    <AnimationPage>
      <LightVersionOverlay active={liteOverlay.active}>
      <SummaryCardRow>
        <SummaryCard>
          <SummaryCardLabel>전체 잠재회원</SummaryCardLabel>
          <SummaryCardValue>{summary?.totalCount ?? 0}</SummaryCardValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryCardLabel>이번달 신규</SummaryCardLabel>
          <SummaryCardValue>{summary?.thisMonthCount ?? 0}</SummaryCardValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryCardLabel>전환확률 높음</SummaryCardLabel>
          <SummaryCardValue>{summary?.highConversionCount ?? 0}</SummaryCardValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryCardLabel>앱 연동</SummaryCardLabel>
          <SummaryCardValue>{summary?.appLinkedCount ?? 0}</SummaryCardValue>
        </SummaryCard>
      </SummaryCardRow>
      <FilterContainer>
        {/* Header: 타이틀 + 중앙 검색 + 접기 아이콘 */}
        <FilterHeader>
          <FilterTitle>{t('상세검색 필터')}</FilterTitle>
          <SearchInputWrapper>
            <SearchInput
              size="small"
              placeholder={t('이름 or 전화번호를 입력해 주세요')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') onSubmitForm();
              }}
            />
            <CommonSearchSubmitButton onClick={onSubmitForm}>{t('검색')}</CommonSearchSubmitButton>
          </SearchInputWrapper>
          {/* <CollapseIcon isCollapsed={isCollapsed} onClick={() => setIsCollapsed(!isCollapsed)} /> */}
        </FilterHeader>

        {/* Content: 접기/펼치기 가능 */}
        <FilterContent isCollapsed={isCollapsed}>
          {/* 필터 1행: 성별 + 연령대 (컴팩트 너비) */}
          <FilterGrid>
            <FilterBlockCompact>
              <FilterSectionTitle>
                <FilterSectionTitleText>{t('Gender')}</FilterSectionTitleText>
              </FilterSectionTitle>
              <ChipsContainer>
                {GENDER_CHIPS.map(chip => (
                  <FilterChip
                    key={chip.value ?? 'all'}
                    isActive={isGenderActive(chip.value)}
                    onClick={() => handleGenderClick(chip.value)}>
                    {t(chip.label)}
                  </FilterChip>
                ))}
              </ChipsContainer>
            </FilterBlockCompact>

            <FilterBlockCompact>
              <FilterSectionTitle>
                <FilterSectionTitleText>{t('Age Group')}</FilterSectionTitleText>
              </FilterSectionTitle>
              <StyledSelect
                displayEmpty
                value={query.ageRanges.length > 0 ? query.ageRanges[0] : ''}
                onChange={handleAgeRangeChange}>
                {AGE_RANGE_OPTIONS.map(option => (
                  <MenuItem key={option.value || 'placeholder'} value={option.value} disabled={option.value === ''}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FilterBlockCompact>

            <FilterBlockCompact>
              <FilterSectionTitle>
                <FilterSectionTitleText>{t('회원 전환 확률')}</FilterSectionTitleText>
              </FilterSectionTitle>
              <CommonSelect
                value={query.conversionRate}
                onChange={handleConversionRateChange}
                emptyLabel="전체"
                width="100%"
                options={CONVERSION_RATE_OPTIONS}
              />
            </FilterBlockCompact>
          </FilterGrid>

          {/* 필터 2행: 방문경로 / 상담수단 / 운동목적 / 앱연동 */}
          <FilterGrid>
            <FilterBlockCompact>
              <FilterSectionTitle>
                <FilterSectionTitleText>{t('방문경로')}</FilterSectionTitleText>
              </FilterSectionTitle>
              <CommonSelect
                value={query.visitPath}
                onChange={handleFilterChange('visitPath')}
                emptyLabel="전체"
                width="100%"
                options={visitPaths}
              />
            </FilterBlockCompact>

            <FilterBlockCompact>
              <FilterSectionTitle>
                <FilterSectionTitleText>{t('상담수단')}</FilterSectionTitleText>
              </FilterSectionTitle>
              <CommonSelect
                value={query.consultMethod}
                onChange={handleFilterChange('consultMethod')}
                emptyLabel="전체"
                width="100%"
                options={consultMethods}
              />
            </FilterBlockCompact>

            <FilterBlockCompact>
              <FilterSectionTitle>
                <FilterSectionTitleText>{t('운동목적')}</FilterSectionTitleText>
              </FilterSectionTitle>
              <CommonSelect
                value={query.exercisePurpose}
                onChange={handleFilterChange('exercisePurpose')}
                emptyLabel="전체"
                width="100%"
                options={exercisePurposes}
              />
            </FilterBlockCompact>

            <FilterBlockCompact>
              <FilterSectionTitle>
                <FilterSectionTitleText>{t('앱 연동')}</FilterSectionTitleText>
              </FilterSectionTitle>
              <CommonSelect
                value={query.appLinked}
                onChange={handleFilterChange('appLinked')}
                emptyLabel="전체"
                width="100%"
                options={APP_LINKED_OPTIONS}
              />
            </FilterBlockCompact>
          </FilterGrid>

          {/* 필터 3행: 기간 (전체 너비) */}
          <FilterGrid>
            <FilterBlock>
              <FilterSectionTitle>
                <FilterSectionTitleText>{t('Period')}</FilterSectionTitleText>
              </FilterSectionTitle>
              <PeriodInputWrapper>
                <FormInputDate
                  name="registeredDateFrom"
                  control={control}
                  placeholder={t('Select date')}
                  renderInput={params => <TextField color="info" size="small" variant="outlined" {...params} />}
                />
                <img src="/images/icons/temp.svg" alt="temp" width="10" height="16" />
                <FormInputDate
                  name="registeredDateTo"
                  control={control}
                  placeholder={t('Select date')}
                  minDate={watch('registeredDateFrom') || null}
                  renderInput={params => <TextField color="info" size="small" variant="outlined" {...params} />}
                />
                <FilterButtons setValue={setValue} watch={watch} />
              </PeriodInputWrapper>
            </FilterBlock>
          </FilterGrid>

          <Divider />

          <FilterActionRow>
            <CommonFilterActionButton variant="primary" onClick={onSubmitForm}>
              {t('Search for members with set conditions')}
            </CommonFilterActionButton>
            <CommonFilterActionButton variant="secondary" onClick={handleReset}>
              {t('Reset search conditions')}
            </CommonFilterActionButton>
          </FilterActionRow>
        </FilterContent>
        <CommonFilterToggle variant="border" open={!isCollapsed} onClick={() => setIsCollapsed(prev => !prev)} />
      </FilterContainer>
      <TableActionRow>
        <TotalCountText>
          총 <TotalCountNumber>{managers?.totalElements || 0}</TotalCountNumber>건
        </TotalCountText>
        <PageSizeWrapper>
          {/* 라이트버전은 클릭 시 안내 오버레이 표시 */}
          <SMSButton onClick={isGuestUser ? liteOverlay.show : () => setSmsModalOpen(true)} variant="outlined">
            <SmsIcon />
            SMS
          </SMSButton>
          <CommonExcelButton onClick={exportToExcel} disabled={isExcelDisabled}>
            {selectedMap.size > 0 ? t('선택 엑셀 다운로드') : t('전체 엑셀 다운로드')}
          </CommonExcelButton>
          <FormInputDropdown
            control={control}
            name="size"
            options={pageSize}
            staticValue={query.limit}
            onChange={changeLimit}
            sx={{ width: '108px', '& .MuiInputBase-input': { borderRadius: '5px' } }}
          />
        </PageSizeWrapper>
      </TableActionRow>
      {isLoading ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : (
        <Box>
          <TemporaryUserListTable
            managers={managersWithCheck}
            handleChange={handleChange}
            selectAllLoading={selectAllLoading}
            sortDirection={query.sortDirection}
            onSortChange={handleSortChange}
            onRowClick={() => sessionStorage.setItem('temporaryUserSearchConditions', JSON.stringify(query))}
          />

          <CommonPagination
            totalElements={managers?.totalElements}
            limit={query.limit}
            page={query.page}
            onChange={handleChangePage}
          />
        </Box>
      )}

      <SmsModal
        open={smsModalOpen}
        onClose={() => setSmsModalOpen(false)}
        selectedMembers={selectedUsers}
        mode="TEMPORARY"
      />
      </LightVersionOverlay>
    </AnimationPage>
  );
};

export default TemporaryUserList;
